import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import currencyService from '../services/currencyService';

// Supported currencies
export type Currency = 'USD' | 'ILS' | 'EUR';

// Currency rate interface
export interface CurrencyRate {
  code: string;
  rate: number;
}

// Context interface
export interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  rates: CurrencyRate[];
  convertPrice: (price: number, fromCurrency?: string) => number;
  formatPrice: (price: number, withSymbol?: boolean) => string;
}

// Create context with default values
const CurrencyContext = createContext<CurrencyContextType>({
  currency: 'ILS',
  setCurrency: () => {},
  rates: [],
  convertPrice: (price) => price,
  formatPrice: (price) => price.toString(),
});

// Currency storage key
const CURRENCY_STORAGE_KEY = 'app_currency';

// Currency symbols map
const currencySymbols: Record<Currency, string> = {
  USD: '$',
  ILS: '₪',
  EUR: '€',
};

/**
 * Currency Provider Component
 * Manages currency selection and conversion rates
 */
export const CurrencyProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  // Get saved currency or default to ILS. Respects a user's explicit prior choice.
  const getSavedCurrency = (): Currency => {
    const saved = localStorage.getItem(CURRENCY_STORAGE_KEY) as Currency;
    return (saved && ['USD', 'ILS', 'EUR'].includes(saved)) ? saved as Currency : 'ILS';
  };
  
  const [currency, setCurrencyState] = useState<Currency>(getSavedCurrency());
  const [rates, setRates] = useState<CurrencyRate[]>([]);
  
  // Save currency preference when it changes
  useEffect(() => {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
    
    // Update Axios default headers with the selected currency
    api.defaults.headers.common['X-Currency'] = currency;
  }, [currency]);
  
  // Fetch currency rates when component mounts
  useEffect(() => {
    const fetchCurrencyRates = async () => {
      try {
        const data = await currencyService.getCurrencyRates();
        if (data && data.currencies) {
          setRates(data.currencies);
        }
      } catch (error) {
        console.error('Failed to fetch currency rates', error);
        // Set some default rates in case the API fails
        setRates([
          { code: 'USD', rate: 1 },
          { code: 'ILS', rate: 3.63 },
          { code: 'EUR', rate: 0.92 }
        ]);
      }
    };
    
    fetchCurrencyRates();
    
    // Refresh rates every hour
    const intervalId = setInterval(fetchCurrencyRates, 60 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Set currency handler
  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
  };
  
  /**
   * Convert price from one currency to the selected currency
   * @param price - Price to convert
   * @param fromCurrency - Source currency (defaults to ILS)
   * @returns Converted price in the selected currency
   */
  const convertPrice = (price: number, fromCurrency: string = 'ILS'): number => {
    if (!price) return 0;
    
    // If source and target currency are the same, no conversion needed
    if (fromCurrency === currency) return price;
    
    // Find rates for the source and target currencies
    const fromRate = rates.find(r => r.code === fromCurrency)?.rate || 1;
    const toRate = rates.find(r => r.code === currency)?.rate || 1;
    
    // Convert: first to USD, then to target currency
    if (fromCurrency !== 'USD') {
      price = price / fromRate; // Convert to USD
    }
    
    if (currency !== 'USD') {
      price = price * toRate; // Convert from USD to target currency
    }
    
    return price;
  };
  
  /**
   * Format price with currency symbol
   * @param price - Price to format
   * @param withSymbol - Whether to include currency symbol
   * @returns Formatted price string
   */
  const formatPrice = (price: number, withSymbol: boolean = true): string => {
    const formattedPrice = price.toFixed(2);
    if (!withSymbol) return formattedPrice;
    
    const symbol = currencySymbols[currency];
    return `${symbol}${formattedPrice}`;
  };
  
  return (
    <CurrencyContext.Provider 
      value={{ 
        currency, 
        setCurrency, 
        rates,
        convertPrice, 
        formatPrice 
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

/**
 * Custom hook to use currency context
 * @returns Currency context values
 */
export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}; 